import React, { useState, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
// import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const Navbar = () => {
  const [activeLink, setActiveLink] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL;
  const [regionOpen, setRegionOpen] = useState(false);
  const regionButtonRef = useRef(null);
  const regionMenuRef = useRef(null);

  // Check if mobile based on screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 992);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Add scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        regionMenuRef.current &&
        regionButtonRef.current &&
        !regionMenuRef.current.contains(event.target) &&
        !regionButtonRef.current.contains(event.target)
      ) {
        setRegionOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const path = window.location.pathname;
    setActiveLink(path);
  }, []);

  const navItems = [
    { path: "/about", label: "About Us" },
    { path: "/services", label: "Services" },
    { path: "/apply-franchise", label: "Franchise" },
    { path: "/events", label: "Events" },
    { path: "/contact", label: "Contact" },
  ];

  const regions = [
    { code: "IN", label: "India", flag: "🇮🇳" },
    { code: "NP", label: "Nepal", flag: "🇳🇵" },
    { code: "LK", label: "Sri Lanka", flag: "🇱🇰" },
    { code: "BD", label: "Bangladesh", flag: "🇧🇩" },
    { code: "AE", label: "UAE", flag: "🇦🇪", path: "/uae" },
  ];

  // Navigation click handler
  const handleNavClick = (path) => {
    window.location.href = path;
    setActiveLink(path);
    setDrawerOpen(false);
  };

  const handleRegionClick = () => {
    setRegionOpen(!regionOpen);
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Desktop navigation items
  const renderDesktopNav = () => (
    <Box className="flex items-center space-x-8 ml-10">
      {" "}
      {/* Left margin added here */}
      {navItems.map((item) => (
        <Box key={item.path} className="relative">
          <Typography
            component="a"
            href={item.path}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick(item.path);
            }}
            className={`inline-flex items-center transition-colors duration-300 px-3 py-2 no-underline font-poppins ${
              activeLink === item.path ? "text-[#4CAF50]" : "text-[#808080]"
            } hover:text-[#4CAF50]`}
            sx={{
              fontWeight: 400,
              fontSize: "16px",
              lineHeight: "100%",
              letterSpacing: "0%",
              textAlign: "center",
            }}
          >
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  // Mobile drawer content
  const renderDrawer = () => (
    <Box
      className={`w-[280px] p-4 transform transition-transform duration-300 ease-in-out ${
        drawerOpen ? "translate-x-0" : "-translate-x-full"
      }`}
      role="presentation"
    >
      <Box className="flex justify-between items-center mb-6">
        <Box
          component="img"
          src={`${imageBaseUrl}logo.png`}
          alt="U Clean Logo"
          className="h-8"
        />
        <Box
          component="button"
          onClick={toggleDrawer}
          className="p-2 text-gray-500 hover:text-gray-700 bg-transparent border-0 outline-none"
          aria-label="close menu"
        >
          <CloseIcon />
        </Box>
      </Box>

      <Box className="space-y-1">
        {navItems.map((item) => (
          <Box key={item.path}>
            <Typography
              component="a"
              href={item.path}
              onClick={(e) => {
                e.preventDefault();
                handleNavClick(item.path);
              }}
              className={`block px-3 py-2 text-base font-medium rounded-md transition-colors duration-300 no-underline font-poppins ${
                activeLink === item.path
                  ? "text-[#4CAF50] bg-green-50"
                  : "text-[#4b5563] hover:text-[#4CAF50] hover:bg-gray-50"
              }`}
            >
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box className="border-t border-gray-200 mt-6 pt-6">
        <Box className="space-y-3">
          <Box className="flex items-center justify-between">
            <Typography className="text-sm text-gray-600 font-poppins">
              Language:
            </Typography>
            <Typography className="text-sm font-medium font-poppins">
              EN 🔄
            </Typography>
          </Box>

          <Box>
            <Typography className="block text-sm text-gray-600 mb-2 font-poppins">
              Select Region:
            </Typography>
            <Box
              component="select"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-poppins"
            >
              {regions.map((region) => (
                <option key={region.code} value={region.code}>
                  {region.flag} {region.label}
                </option>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Top Banner */}
      <Box className="bg-gray-100 text-center py-2 text-sm text-gray-600 border-b">
        <Box className="flex items-center justify-center">
          <Box
            component="img"
            src={`${imageBaseUrl}navIcon.png`}
            alt="Career"
            sx={{
              width: 16,
              height: 16,
              marginRight: 1,
            }}
          />
          <Typography
            sx={{
              fontFamily: "Poppins",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "100%",
              letterSpacing: "0%",
              color: "#4d4d4d",
            }}
            className="font-poppins"
          >
            Nationwide Free Pickup and Delivery
          </Typography>
        </Box>
      </Box>

      <Box
        component="nav"
        className={`sticky top-0 bg-white shadow-none z-50 transition-shadow duration-300 ${
          isScrolled ? "shadow-sm" : "border-b"
        }`}
      >
        <Box className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Box className="flex justify-between items-center h-16 sm:h-20">
            {/* Logo */}
            <Box className="flex-shrink-0">
              <Typography
                component="a"
                href="/"
                className="flex items-center no-underline"
              >
                <Box
                  component="img"
                  src={`${imageBaseUrl}logo.png`}
                  alt="U Clean Logo"
                  className="h-10 w-auto transition-all duration-300"
                />
              </Typography>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <>
                {/* Center Navigation */}
                <Box className="hidden lg:flex items-center">
                  {renderDesktopNav()}
                </Box>

                {/* Right Side Elements */}
                <Box className="flex items-center space-x-4">
                  {/* Language Selector */}
                  {/* <Box className="flex items-center text-sm text-gray-600 gap-1">
                    <Typography variant="body2" className="mr-1 font-poppins">
                      EN
                    </Typography>
                    <Box
                      component="img"
                      src={`${imageBaseUrl}navGlobe.png`}
                      alt="U Clean Logo"
                      sx={{
                        width: 16,
                        height: 16,
                        marginRight: 1,
                      }}
                    />
                  </Box> */}

                  {/* Region Selector */}
                  <Box className="relative">
                    <Box
                      component="button"
                      ref={regionButtonRef}
                      onClick={handleRegionClick}
                      className="flex items-center px-6 py-3.5 rounded-md text-sm hover:bg-gray-50 transition-colors duration-300 bg-transparent outline-none"
                      sx={{
                        border: "1px solid #65BA0D",
                        boxShadow: "0px 4px 4px 0px #00000014",
                        color: "#65BA0D",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Poppins",
                          fontWeight: 500,
                          fontSize: "18px",
                          lineHeight: "100%",
                          letterSpacing: "0%",
                          textAlign: "center",
                          color: "#65BA0D",
                        }}
                      >
                        Select Your Region
                      </Typography>

                      {/* <ExpandMoreIcon
                        className={`ml-2 transition-transform duration-200 ${
                          regionOpen ? 'rotate-180' : ''
                        }`}
                        sx={{ fontSize: 16, color: '#65BA0D' }}
                      /> */}
                    </Box>

                    {regionOpen && (
                      <Box
                        ref={regionMenuRef}
                        className="absolute top-full right-0 mt-1 w-48 bg-white rounded-md shadow-lg border z-50"
                      >
                        {regions.map((region) => (
                          <Box
                            key={region.code}
                            component="button"
                            onClick={() => {
                              // Handle region selection
                              setRegionOpen(false);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-300 bg-transparent border-0 outline-none"
                          >
                            <Typography variant="body2" className="mr-2">
                              {region.flag}
                            </Typography>
                            <Typography
                              variant="body2"
                              className="font-poppins"
                            >
                              {region.label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              </>
            )}

            {/* Mobile menu button */}
            {isMobile && (
              <Box
                component="button"
                onClick={toggleDrawer}
                className={`p-2 text-[#4b5563] transition-transform duration-200 bg-transparent border-0 outline-none ${
                  drawerOpen ? "rotate-90" : "rotate-0"
                }`}
                aria-label="menu"
              >
                <MenuIcon />
              </Box>
            )}
          </Box>
        </Box>

        {/* Mobile drawer overlay and content */}
        <Box
          className={`fixed inset-0 z-50 transition-opacity duration-300 ${
            drawerOpen ? "opacity-100 visible" : "opacity-0 invisible"
          }`}
        >
          <Box
            className="absolute inset-0 bg-black transition-opacity duration-300"
            style={{ opacity: drawerOpen ? 0.5 : 0 }}
            onClick={toggleDrawer}
          />
          <Box
            className="absolute inset-y-0 left-0 bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            {renderDrawer()}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Navbar;
